import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from 'components/Button';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { useCurrentInstallationState } from 'hooks/useCurrentInstallationState';
import {
  CREATE_INSTALLATION_PREFERENCES,
  SAVE_INSTALLATION_PREFERENCES,
} from 'gql/mutations';
import {
  GET_INSTALLATION_PREFERENCES,
  GET_USER_ROLE,
} from 'gql/queries';
import { InstallationDraft } from 'types/installationPresentation';
import { StoreValues } from 'types/store';
import { isShipaidAdmin } from 'utils';
import GuidedInstallationPreferences from './GuidedInstallationPreferences';
import { ApprovedCheckoutTitleSelect } from './CheckoutCustomization';
import { CHECKOUT_TITLE_REVIEW_MESSAGE, hasApprovedCheckoutTitles } from 'utils/checkoutTitlePolicy';
import {
  createInstallationPreferences,
  INSTALLATION_CART_OPTIONS,
  summarizeExistingInstallation,
} from 'utils/installationPreferences';
import {
  confirmInstallationDraftCreate,
  confirmInstallationDraftSave,
  parseInstallationDraftRecord,
  PersistedInstallationDraft,
  prepareInstallationDraftForPersistence,
} from 'utils/installationDraftPersistence';

interface PreferenceRecord {
  storeId: string;
  revision: number;
  preferences: unknown;
}

const selectClass =
  'mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:opacity-60';

const optionLabel = (children: React.ReactNode): string =>
  React.Children.toArray(children).map((child) =>
    typeof child === 'string' || typeof child === 'number' ? String(child) : '',
  ).join('');

const PreferenceSelect = ({
  id, compactLabels, children, ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & {
  id: string;
  compactLabels: Record<string, string>;
  value: React.SelectHTMLAttributes<HTMLSelectElement>['value'];
  'aria-describedby'?: string;
}) => {
  const options = React.Children.toArray(children).filter(
    (child): child is React.ReactElement<React.OptionHTMLAttributes<HTMLOptionElement>> =>
      React.isValidElement(child),
  );
  const selected = options.find((option) => option.props.value === props.value);
  const descriptionId = `${id}-selection`;
  return <>
    <select {...props} id={id} aria-describedby={
      [props['aria-describedby'], descriptionId].filter(Boolean).join(' ')
    }>
      {options.map((option) => React.cloneElement(option, {
        label: compactLabels[String(option.props.value)] ?? optionLabel(option.props.children),
        'aria-label': optionLabel(option.props.children),
      }))}
    </select>
    <span id={descriptionId} className="mt-1 block whitespace-normal break-words text-xs font-normal text-slate-600">
      {optionLabel(selected?.props.children)}
    </span>
  </>;
};

const nullableChoice = (value: string): boolean | null =>
  value === 'keep-existing' ? null : value === 'enabled';

interface DraftEditorProps {
  guided?: boolean;
  storeId: string;
  store: StoreValues;
  record: PersistedInstallationDraft | null;
  canEdit: boolean;
  onReload: () => Promise<any>;
}

const DraftEditor = ({
  guided = false,
  storeId,
  store,
  record,
  canEdit,
  onReload,
}: DraftEditorProps) => {
  const initial = record?.preferences ?? createInstallationPreferences();

  const [preferences, setPreferences] = useState<InstallationDraft>(initial);
  const [savedPreferences, setSavedPreferences] = useState<InstallationDraft>(initial);
  const [revision, setRevision] = useState<number | undefined>(
    record?.revision,
  );
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [invalidTitle, setInvalidTitle] = useState<string | null>(null);
  const [presentationVersion, setPresentationVersion] = useState(0);

  const isSavingRef = useRef(false);
  const isReloadingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [create] = useMutation(CREATE_INSTALLATION_PREFERENCES);
  const [save] = useMutation(SAVE_INSTALLATION_PREFERENCES);
  const current = useCurrentInstallationState(storeId, store);
  const existing = current.usesCompanion ? summarizeExistingInstallation({
    checkoutPlus: { enabled: current.checkoutPlus }, addToCartPlus: { enabled: current.addToCart },
  }) : summarizeExistingInstallation(store);
  const shownMethod = preferences.installationMethod === 'keep-existing'
    ? current.installationMethod ?? 'keep-existing' : preferences.installationMethod;
  const shownAddToCart = preferences.addToCart ?? current.addToCart;
  const historicalCartPreference = preferences.cartExperience === 'cart-widget' || preferences.cartExperience === 'none';

  const dirty =
    JSON.stringify(preferences) !== JSON.stringify(savedPreferences);
  const titleNeedsReview = !hasApprovedCheckoutTitles(preferences);
  const customizationInvalid = invalidTitle !== null || titleNeedsReview;
  const hasUnsavedChanges = dirty || customizationInvalid;

  const update = <K extends keyof InstallationDraft>(
    key: K,
    value: InstallationDraft[K],
  ) => {
    if (isSavingRef.current || isReloadingRef.current) return;
    setPreferences((prev) => ({ ...prev, [key]: value }));
    setMessage('');
    if (!conflict) setError('');
  };

  const handleSave = async (): Promise<boolean> => {
    if (
      isSavingRef.current ||
      isReloadingRef.current ||
      !canEdit ||
      conflict ||
      customizationInvalid ||
      !dirty
    ) {
      return false;
    }
    const draft = prepareInstallationDraftForPersistence(preferences);
    if (!draft) {
      setError('Preferences could not be saved. Your live installation is unchanged. Please review your draft and try again.');
      return false;
    }

    isSavingRef.current = true;
    setSaving(true);
    setError('');
    setMessage('');

    try {
      let result: PersistedInstallationDraft | null;

      if (revision === undefined) {
        const response = await create({
          variables: { storeId, preferences: draft },
        });
        if (!mountedRef.current) return false;

        result = confirmInstallationDraftCreate(response.data?.insertStoreInstallationPreferencesOne, storeId, draft);
      } else {
        const response = await save({
          variables: { storeId, revision, preferences: draft },
        });
        if (!mountedRef.current) return false;

        const updateResult = response.data?.updateStoreInstallationPreferences;
        if (updateResult?.affected_rows === 0) {
          setConflict(true);
          setError(
            'Preferences changed in another session or access changed. Reload saved preferences before saving again.',
          );
          return false;
        }

        result = confirmInstallationDraftSave(updateResult, storeId, revision, draft);
      }

      if (!result) {
        throw new Error('Draft save result missing');
      }

      setRevision(result.revision);
      setSavedPreferences(draft);
      setPreferences(draft);
      setMessage(
        'Preferences saved as a draft. Your live installation is unchanged.',
      );
      return true;
    } catch {
      if (mountedRef.current) {
        setError(
          'Preferences could not be saved. Your live installation is unchanged. Reload saved preferences to check for another edit, or try again.',
        );
      }
      return false;
    } finally {
      isSavingRef.current = false;
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  };

  const handleReload = async () => {
    if (isSavingRef.current || isReloadingRef.current) return;
    isReloadingRef.current = true;
    setReloading(true);

    try {
      const res = await onReload();
      if (!mountedRef.current) return;

      const rec = res?.data?.storeInstallationPreferences;
      if (rec === null) {
        const fresh = createInstallationPreferences();
        setPreferences(fresh);
        setSavedPreferences(fresh);
        setRevision(undefined);
        setConflict(false);
        setError('');
        setMessage('');
        setInvalidTitle(null);
        setPresentationVersion(value => value + 1);
      } else if (rec) {
        const parsed = parseInstallationDraftRecord(rec, storeId);
        if (!parsed) {
          throw new Error('Malformed reloaded preferences');
        }
        setPreferences(parsed.preferences);
        setSavedPreferences(parsed.preferences);
        setRevision(parsed.revision);
        setConflict(false);
        setError('');
        setMessage('');
        setInvalidTitle(null);
        setPresentationVersion(value => value + 1);
      } else {
        throw new Error('Missing reloaded preferences record');
      }
    } catch {
      if (mountedRef.current) {
        setError(
          'Failed to reload saved preferences. Your live installation is unchanged. Please try again.',
        );
      }
    } finally {
      isReloadingRef.current = false;
      if (mountedRef.current) {
        setReloading(false);
      }
    }
  };

  const handleDiscard = () => {
    if (
      isSavingRef.current ||
      isReloadingRef.current ||
      !canEdit ||
      !hasUnsavedChanges ||
      conflict
    ) {
      return;
    }
    setPreferences(savedPreferences);
    setInvalidTitle(null);
    setPresentationVersion(value => value + 1);
    setError('');
    setMessage('');
  };

  return (
    <>
      {!guided && titleNeedsReview && <div className="mb-4 rounded-lg bg-red-50 p-3">
        <p role="alert" className="text-sm text-red-700">{CHECKOUT_TITLE_REVIEW_MESSAGE}</p>
        <ApprovedCheckoutTitleSelect preferences={preferences} disabled={!canEdit || saving || conflict || reloading}
          onChange={setPreferences} />
      </div>}
      {guided ? <GuidedInstallationPreferences
        onSave={handleSave}
        presentationVersion={presentationVersion}
        preferences={preferences}
        savedPreferences={savedPreferences}
        store={store}
        current={current}
        existing={existing}
        disabled={!canEdit || saving || conflict || reloading}
        canManageFeatures={canEdit}
        dirty={hasUnsavedChanges}
        featureBlockReason={saving ? 'Installation preferences are saving. Wait for confirmation before editing features.'
          : reloading ? 'Saved preferences are reloading. Wait for them before editing features.'
            : conflict ? 'Installation preferences changed or access changed. Reload saved preferences below before editing features.'
              : customizationInvalid ? 'Choose approved checkout wording before saving or editing features.'
                : dirty ? 'Feature editing is paused. Save preferences or Discard changes below, then return here.' : null}
        invalidTitle={invalidTitle ?? (titleNeedsReview ? 'saved-title-needs-review' : null)}
        onInvalidTitleChange={(title) => {
          if (!canEdit || conflict || isSavingRef.current || isReloadingRef.current) return;
          setInvalidTitle(title);
          setMessage('');
        }}
        onChange={(next) => {
          if (!canEdit || conflict || isSavingRef.current || isReloadingRef.current) return;
          setPreferences(next);
          setMessage('');
          setError('');
        }}
      /> : <>
      <div className="mb-5 rounded-md bg-slate-50 p-3 text-sm text-slate-700">
        <p className="font-medium">Current configuration</p>
        <p>
          Checkout Plus: {existing.checkoutPlus}. Add to Cart+:{' '}
          {existing.addToCart}.
        </p>
        <p className="mt-1">
          These settings do not confirm placement or cart operation. Existing
          custom placements are preserved.
        </p>
        <p className="mt-1">
          Confirmed configuration choices are preselected unless you choose a preference. Program, cart
          layout, placement, appearance and native checkout activation remain
          unverified. Saving a draft does not install or verify them.
        </p>
        <Button variant="outlined" disabled={!current.canRefresh} onClick={current.refresh}>
          Refresh current configuration
        </Button>
        {current.checking && <p role="status" className="mt-1">Checking the published theme configuration…</p>}
      </div>

      <fieldset
        disabled={!canEdit || saving || conflict || reloading}
        className="space-y-5"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block text-sm font-medium text-slate-800">
            Who pays for the guarantee?
            <PreferenceSelect
              id={`${storeId}-payer`}
              compactLabels={{ 'keep-existing': 'Choose program', customer: 'Customer-paid · Optional', merchant: 'Merchant-paid · Included' }}
              aria-label="Who pays for the guarantee?"
              className={selectClass}
              value={preferences.payer}
              onChange={(event) =>
                update(
                  'payer',
                  event.target.value as InstallationDraft['payer'],
                )
              }
            >
              <option value="keep-existing">Choose a program (current unverified)</option>
              <option value="customer">Customer-paid optional guarantee</option>
              <option value="merchant">Merchant-paid included guarantee</option>
            </PreferenceSelect>
          </label>
          <label className="block text-sm font-medium text-slate-800">
            Installation method
            <PreferenceSelect
              id={`${storeId}-installation-method`}
              compactLabels={{
                'keep-existing': 'Choose method',
                theme: 'Shopify theme',
                headless: 'Headless / custom', assisted: 'ShipAid-assisted setup',
              }}
              aria-label="Installation method"
              className={selectClass}
              value={shownMethod}
              onChange={(event) =>
                update(
                  'installationMethod',
                  event.target
                    .value as InstallationDraft['installationMethod'],
                )
              }
            >
              <option value="keep-existing">Choose installation method (current unverified)</option>
              <option value="theme">Shopify theme integration</option>
              <option value="headless">Headless / custom storefront</option>
              <option value="assisted">Have ShipAid help with setup</option>
            </PreferenceSelect>
          </label>
        </div>

        {preferences.payer === 'merchant' && (
          <p className="text-sm text-slate-600">
            Merchant-paid coverage and billing require a separate setup. This
            preference does not cover orders or change shopper charges.
          </p>
        )}
        {preferences.installationMethod === 'headless' && (
          <p className="text-sm text-slate-600">
            Headless requires a developer integration with your existing cart.
            Saving this preference does not install the integration.
          </p>
        )}
        {preferences.installationMethod === 'assisted' && (
          <p className="text-sm text-slate-600">
            Your preferred setup will be saved for review. No support request is
            sent by this action.
          </p>
        )}

        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-slate-900">
            Cart experience
          </legend>
          <div className="grid gap-2 md:grid-cols-2">
            {INSTALLATION_CART_OPTIONS.filter((option) =>
              option.value === 'checkout-plus-buttons' || option.value === 'checkout-plus-link',
            ).map((option) => (
              <label
                key={option.value}
                className={`flex gap-3 rounded-md border p-3 text-sm ${
                  preferences.cartExperience === option.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200'
                }`}
              >
                <input
                  type="radio"
                  name={`cart-experience-${storeId}`}
                  aria-label={option.label}
                  className="mt-1"
                  checked={preferences.cartExperience === option.value}
                  onChange={() => update('cartExperience', option.value)}
                />
                <span>
                  <span className="block font-medium text-slate-900">
                    {option.label}
                  </span>
                  <span className="mt-1 block text-slate-600">
                    {option.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
          {historicalCartPreference && <p className="mt-2 text-sm text-slate-700">
            Saved private cart preference: {preferences.cartExperience === 'cart-widget' ? 'Cart Widget' : 'No cart offer'}.
            {' '}This saved intent does not verify the current installation and is preserved until you choose a different draft layout.
          </p>}
          <p className="mt-2 text-sm text-slate-600">
            Current cart experience is unverified. Existing Widget V1, Checkout
            V1 and other custom setups are preserved. Choose Checkout+ only to
            record a new private draft.
          </p>
          {preferences.payer === 'merchant' && (
            <p className="mt-2 text-sm text-slate-600">
              Paid opt-in layouts are recorded for review only. Merchant-paid
              setup needs included-guarantee messaging.
            </p>
          )}
        </fieldset>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block text-sm font-medium text-slate-800">
            Add to Cart+
            <PreferenceSelect
              id={`${storeId}-add-to-cart`}
              compactLabels={{
                'keep-existing': 'Choose Add to Cart+',
                enabled: 'Include',
              }}
              aria-label="Add to Cart+"
              className={selectClass}
              value={
                shownAddToCart === null
                  ? 'keep-existing'
                  : shownAddToCart
                  ? 'enabled'
                  : 'disabled'
              }
              onChange={(event) =>
                update('addToCart', nullableChoice(event.target.value))
              }
            >
              <option value="keep-existing">Choose Add to Cart+ selection (current unverified)</option>
              <option value="enabled">Include in preferred setup</option>
              <option value="disabled">Do not include</option>
            </PreferenceSelect>
          </label>
          <label className="block text-sm font-medium text-slate-800">
            Shopify Plus checkout extension
            <PreferenceSelect
              id={`${storeId}-native-checkout`}
              compactLabels={{ 'keep-existing': 'Choose native checkout' }}
              aria-label="Shopify Plus checkout extension"
              className={selectClass}
              value={
                preferences.checkoutExtension === null
                  ? 'keep-existing'
                  : preferences.checkoutExtension
                  ? 'enabled'
                  : 'disabled'
              }
              onChange={(event) =>
                update(
                  'checkoutExtension',
                  nullableChoice(event.target.value),
                )
              }
            >
              <option value="keep-existing">Choose native checkout selection (current unverified)</option>
              <option
                value="enabled"
                disabled={
                  store.isStoreShopifyPlus !== true &&
                  savedPreferences?.checkoutExtension !== true
                }
              >
                Include in preferred setup
              </option>
              <option value="disabled">Do not include</option>
            </PreferenceSelect>
          </label>
          <label className="block text-sm font-medium text-slate-800">
            Cart placement
            <PreferenceSelect
              id={`${storeId}-placement`}
              compactLabels={{ 'keep-existing': 'Choose location' }}
              aria-label="Cart placement"
              className={selectClass}
              value={preferences.placement}
              onChange={(event) =>
                update(
                  'placement',
                  event.target.value as InstallationDraft['placement'],
                )
              }
            >
              <option value="keep-existing">Choose display location (current unverified)</option>
              <option value="cart-page">Cart page</option>
              <option value="cart-drawer">Cart drawer</option>
              <option value="cart-page-and-drawer">Cart page and drawer</option>
            </PreferenceSelect>
          </label>
          {preferences.schemaVersion === 1 ? <label className="block text-sm font-medium text-slate-800">
            Appearance preference
            <PreferenceSelect
              id={`${storeId}-appearance`}
              compactLabels={{ 'preserve-existing': 'Choose appearance' }}
              aria-label="Appearance preference"
              className={selectClass}
              value={preferences.appearance.mode}
              onChange={(event) =>
                update('appearance', {
                  mode: event.target
                    .value as 'inherit-theme' | 'preserve-existing',
                })
              }
            >
              <option value="preserve-existing">
                Choose appearance (current unverified)
              </option>
              <option value="inherit-theme">Match my theme</option>
            </PreferenceSelect>
          </label> : <p className="text-sm text-slate-700">
            Saved draft appearance: {preferences.appearance.mode === 'match-theme' ? 'Match my theme'
              : preferences.appearance.mode === 'custom' ? 'Custom appearance' : 'Preserve current appearance'}.
          </p>}
        </div>

        <p className="text-sm text-slate-600">
          Native checkout placement requires Shopify Plus and a separate
          checkout-editor activation. Theme and cart compatibility still need
          verification.
        </p>
      </fieldset>
      </>}

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-800"
        >
          {error}
        </p>
      )}

      {message && (
        <p role="status" className="mt-4 text-sm text-green-800">
          {message}
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          onClick={handleSave}
          variant={guided ? 'outlined' : undefined}
          isLoading={saving}
          disabled={!canEdit || !dirty || conflict || saving || reloading || customizationInvalid}
        >
          Save preferences
        </Button>
        <Button
          variant="outlined"
          disabled={!canEdit || !hasUnsavedChanges || saving || conflict || reloading}
          onClick={handleDiscard}
        >
          Discard changes
        </Button>
        {error && (
          <Button
            variant="outlined"
            disabled={saving || reloading}
            onClick={handleReload}
          >
            Reload saved preferences
          </Button>
        )}
      </div>
    </>
  );
};

interface StorePreferencesProps {
  guided?: boolean;
  storeId: string;
  store: StoreValues;
  user: { id: string; [key: string]: any };
  isShipAidAdmin: boolean;
}

const StorePreferences = ({
  guided,
  storeId,
  store,
  user,
  isShipAidAdmin,
}: StorePreferencesProps) => {
  const {
    data: prefData,
    loading: prefLoading,
    error: prefError,
    refetch: refetchPreferences,
  } = useQuery<{
    storeInstallationPreferences?: PreferenceRecord | null;
  }>(GET_INSTALLATION_PREFERENCES, {
    variables: { storeId },
    fetchPolicy: 'no-cache',
    context: { queryDeduplication: false },
  });

  const {
    data: roleData,
    loading: roleLoading,
    error: roleError,
  } = useQuery<{
    storeRoles?: Array<{ role: string }>;
  }>(GET_USER_ROLE, {
    variables: { storeId, userId: user.id },
    fetchPolicy: 'no-cache',
    context: { queryDeduplication: false },
  });

  const hasInitialLoadRef = useRef(false);
  const initialRecordRef = useRef<PersistedInstallationDraft | null>(null);
  const isRetryingRef = useRef(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleRetry = async () => {
    if (isRetryingRef.current) return;
    isRetryingRef.current = true;
    setRetrying(true);

    try {
      const res = await refetchPreferences();
      if (!mountedRef.current) return;
      if (
        res?.data &&
        'storeInstallationPreferences' in res.data &&
        res.data.storeInstallationPreferences !== undefined
      ) {
        setRetryError(false);
      } else {
        setRetryError(true);
      }
    } catch {
      if (!mountedRef.current) return;
      setRetryError(true);
    } finally {
      isRetryingRef.current = false;
      if (mountedRef.current) {
        setRetrying(false);
      }
    }
  };

  if (!hasInitialLoadRef.current) {
    if (prefLoading) {
      return (
        <p role="status" className="text-sm text-slate-600">
          Loading installation preferences…
        </p>
      );
    }

    if (
      prefError ||
      retryError ||
      !prefData ||
      prefData.storeInstallationPreferences === undefined
    ) {
      return (
        <div>
          <p role="alert" className="mb-3 text-sm text-slate-700">
            Installation preferences are unavailable. Your current installation is
            unchanged.
          </p>
          <Button
            variant="outlined"
            disabled={retrying}
            onClick={handleRetry}
          >
            Retry preferences
          </Button>
        </div>
      );
    }

    const rawRecord = prefData.storeInstallationPreferences;

    if (rawRecord && rawRecord.storeId !== storeId) {
      return (
        <p role="status" className="text-sm text-slate-600">
          Loading the selected store’s preferences…
        </p>
      );
    }

    const parsedRecord = rawRecord === null ? null : parseInstallationDraftRecord(rawRecord, storeId);
    const isInvalidRecord = rawRecord !== null && parsedRecord === null;

    if (isInvalidRecord) {
      return (
        <div>
          <p
            role="alert"
            className="mb-3 rounded-md bg-amber-50 p-3 text-sm text-amber-900"
          >
            These saved preferences cannot be edited by this version. Your live
            installation is unchanged.
          </p>
          <Button
            variant="outlined"
            disabled={retrying}
            onClick={handleRetry}
          >
            Reload saved preferences
          </Button>
        </div>
      );
    }

    hasInitialLoadRef.current = true;
    initialRecordRef.current = parsedRecord;
  }

  if (hasInitialLoadRef.current && prefData) {
    const latestRecord = prefData.storeInstallationPreferences;
    const parsedRecord = parseInstallationDraftRecord(latestRecord, storeId);
    if (latestRecord === null || parsedRecord) initialRecordRef.current = parsedRecord;
  }

  const memberRole = roleData?.storeRoles?.[0]?.role;
  const canEdit =
    isShipAidAdmin ||
    (!roleLoading &&
      !roleError &&
      (memberRole === 'ADMIN' || memberRole === 'MANAGER'));

  return (
    <>
      {Boolean(roleError) && !isShipAidAdmin && (
        <p
          role="alert"
          className="mb-3 rounded-md bg-amber-50 p-3 text-sm text-amber-900"
        >
          We couldn&apos;t confirm your editing access. Open another page, then
          return here to try again. Your live installation is unchanged.
        </p>
      )}
      <DraftEditor
        guided={guided}
        storeId={storeId}
        store={store}
        record={initialRecordRef.current}
        canEdit={canEdit}
        onReload={refetchPreferences}
      />
    </>
  );
};

const InstallationPreferencesPanel = ({ guided = false }: { guided?: boolean }) => {
  const { storeId, storeProperties, storeLoading } = useStore();
  const { user, role: contextRole } = useUser();
  const isAdmin = isShipaidAdmin(user);

  if (
    !storeId ||
    storeLoading ||
    !storeProperties ||
    storeProperties.id !== storeId ||
    !user ||
    !user.id
  ) {
    return (
      <section
        aria-label="Installation preferences"
        className="mb-8 rounded-lg border border-slate-200 bg-white p-4 sm:p-5"
      >
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-slate-950">
            Installation preferences
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Choose your preferred setup. Saving preferences does not change your
            live store, carts, checkout, pricing, or billing.
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Draft only. Customization, activation, and verification follow in
            separate steps.
          </p>
        </div>
        <p role="status" className="text-sm text-slate-600">
          Select a store and wait for its settings to load.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Installation preferences"
      className="mb-8 rounded-lg border border-slate-200 bg-white p-4 sm:p-5"
    >
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-slate-950">
          {guided ? 'Install & customize' : 'Installation preferences'}
        </h2>
        {guided ? <p className="mt-1 text-sm text-slate-600">Plan your setup. Save a draft before making changes to your store.</p> : <>
        <p className="mt-1 text-sm text-slate-600">
          Choose your preferred setup. Saving preferences does not change your
          live store, carts, checkout, pricing, or billing.
        </p>
        <p className="mt-1 text-sm text-slate-600">
          Draft only. Customization, activation, and verification follow in
          separate steps.
        </p>
        </>}
      </div>
      <StorePreferences
        guided={guided}
        key={`${storeId}:${user.id}:${contextRole}:${isAdmin}`}
        storeId={storeId}
        store={storeProperties}
        user={user}
        isShipAidAdmin={isAdmin}
      />
    </section>
  );
};

export default InstallationPreferencesPanel;
